import { SignalVerdict } from 'app/modules/reports/types'
import { logger } from 'app/components'

export const PREDICTIVE_AI_ENGINE = 'OPSWAT Predictive Alin AI'
export const PROVIDER_METADEFENDER = 'OpswatMetadefender'
export const PROVIDER_REPUTATION = 'OpswatReputation'

export const isPredictiveAIEngine = (name: string): boolean => name === PREDICTIVE_AI_ENGINE

export function sortWithPredictiveAIFirst<T>(items: T[], getKey: (item: T) => string): T[] {
  return [...items].sort((a, b) =>
    getKey(a) === PREDICTIVE_AI_ENGINE ? -1 : getKey(b) === PREDICTIVE_AI_ENGINE ? 1 : 0
  )
}

enum OpswatVerdict {
  NoThreatDetected, // 0
  Infected, // 1
  Suspicious, // 2
  Failed, // 3
  Whitelisted, // 7
  Blacklisted, // 8
  ExceededArchiveDepth, // 9
  NotScanned, // 10
  EncryptedArchive, // 12
  ExceededArchiveSize, // 13
  ExceededArchiveFileNumber, // 14
  PasswordProtectedDocument, // 15
  ExceededArchiveTimeout, // 16
  Mismatch, // 17
  PotentiallyVulnerableFile, // 18
  Cancelled, // 19
  SensitiveDataFound, // 20
  YaraRuleMatched, // 21
  PotentiallyUnwanted, // 22
  UnsupportedFileType, // 23
  InProgress, // 255
}

const opswatVerdictMap: { [key: string]: OpswatVerdict } = {
  '0': OpswatVerdict.NoThreatDetected,
  '1': OpswatVerdict.Infected,
  '2': OpswatVerdict.Suspicious,
  '3': OpswatVerdict.Failed,
  '7': OpswatVerdict.Whitelisted,
  '8': OpswatVerdict.Blacklisted,
  '9': OpswatVerdict.ExceededArchiveDepth,
  '10': OpswatVerdict.NotScanned,
  '12': OpswatVerdict.EncryptedArchive,
  '13': OpswatVerdict.ExceededArchiveSize,
  '14': OpswatVerdict.ExceededArchiveFileNumber,
  '15': OpswatVerdict.PasswordProtectedDocument,
  '16': OpswatVerdict.ExceededArchiveTimeout,
  '17': OpswatVerdict.Mismatch,
  '18': OpswatVerdict.PotentiallyVulnerableFile,
  '19': OpswatVerdict.Cancelled,
  '30': OpswatVerdict.SensitiveDataFound,
  '21': OpswatVerdict.YaraRuleMatched,
  '22': OpswatVerdict.PotentiallyUnwanted,
  '23': OpswatVerdict.UnsupportedFileType,
  '255': OpswatVerdict.InProgress,
}

export function getOPSWATVerdict(status: number) {
  return opswatVerdictMap[status + '']
}

/**
 * Checks whether the scan result is infected
 *
 * @param status Opswat reputation api - individual av engines response
 */
export const isMalicious = (status: number): boolean => {
  return [OpswatVerdict.Blacklisted, OpswatVerdict.Infected].includes(getOPSWATVerdict(status))
}

export const isBenign = (status: number): boolean => {
  return getOPSWATVerdict(status) === OpswatVerdict.Whitelisted
}

export const isInformational = (status: number): boolean => {
  return getOPSWATVerdict(status) === OpswatVerdict.NoThreatDetected
}

const isSuspicious = (status: number): boolean => {
  const name = getOPSWATVerdict(status)

  return [
    OpswatVerdict.Suspicious,
    OpswatVerdict.PotentiallyVulnerableFile,
    OpswatVerdict.PotentiallyUnwanted,
    OpswatVerdict.YaraRuleMatched,
    OpswatVerdict.SensitiveDataFound,
  ].includes(name)
}

/**
 * Whether the OPSWAT scan result should count as a detection (malicious or suspicious).
 * Used to derive detected-count metrics from a mixed set of scan statuses.
 */
export const isDetected = (status: number): boolean => {
  return isMalicious(status) || isSuspicious(status)
}

/**
 * Converts OPSWAT result to SignalVerdict
 *
 * @param status OPSWAT scan result index
 * @returns SignalVerdict
 */
export const getVerdictFromStatus = (status: number): SignalVerdict => {
  if (isBenign(status)) return SignalVerdict.Benign
  if (isInformational(status)) return SignalVerdict.No_threat
  if (isSuspicious(status)) return SignalVerdict.Suspicious
  if (isMalicious(status)) return SignalVerdict.Malicious
  else return SignalVerdict.Unknown
}

/**
 * Verdict reported by the OPSWAT Predictive AI (Predictive Alin AI) engine.
 */
export enum PredictiveAIVerdict {
  No_threat = 'clean', // no threat detected
  Malicious = 'infected', // confirmed threat
  Unknown = 'not_confident', // undetermined
}

/**
 * Converts an OPSWAT scan result index (see opswatVerdictMap, e.g. 0, 1, 23) into a
 * Predictive AI verdict, reusing getVerdictFromStatus for the underlying classification.
 *
 * @param status OPSWAT scan result index
 */
export const getPredictiveAIVerdict = (status: number): PredictiveAIVerdict => {
  switch (status) {
    case 0:
      return PredictiveAIVerdict.No_threat
    case 1:
      return PredictiveAIVerdict.Malicious
    default:
      return PredictiveAIVerdict.Unknown
  }
}

/**
 * Maps a Predictive AI verdict onto the equivalent SignalVerdict so it can reuse the shared
 * verdict formatting and coloring (e.g. Clean is rendered as "No Threat Detected").
 *
 * @param verdict Predictive AI verdict
 */
export const predictiveAIVerdictToSignalVerdict = (verdict: PredictiveAIVerdict): SignalVerdict => {
  switch (verdict) {
    case PredictiveAIVerdict.No_threat:
      return SignalVerdict.No_threat
    case PredictiveAIVerdict.Malicious:
      return SignalVerdict.Malicious
    case PredictiveAIVerdict.Unknown:
    default:
      return SignalVerdict.Unknown
  }
}

/**
 * Final verdicts a Predictive Alin AI result is allowed to be displayed alongside. `null` means the
 * AI verdict is always shown unchanged, regardless of the final verdict.
 */
const predictiveAiAgreeingVerdicts: Record<PredictiveAIVerdict, SignalVerdict[] | null> = {
  [PredictiveAIVerdict.No_threat]: [SignalVerdict.Benign, SignalVerdict.No_threat, SignalVerdict.Informational],
  [PredictiveAIVerdict.Malicious]: [SignalVerdict.Malicious, SignalVerdict.Likely_malicious],
  [PredictiveAIVerdict.Unknown]: null,
}

/**
 * Resolves how a Predictive Alin AI result should be displayed on the Aether layers. The AI verdict is
 * shown unchanged only when it agrees with the final verdict; otherwise it is surfaced as
 * "Not Confident" (undetermined) so it does not contradict the final verdict.
 *
 * The OSINT tab is unaffected and always shows the raw engine result.
 */
export const resolveDisplayPredictiveAiVerdict = (
  predictiveAiVerdict: PredictiveAIVerdict | undefined,
  finalVerdict: SignalVerdict | null | undefined
): { signalVerdict?: SignalVerdict; labelVerdict?: PredictiveAIVerdict } => {
  if (!predictiveAiVerdict) {
    return {}
  }

  const agreeingVerdicts = predictiveAiAgreeingVerdicts[predictiveAiVerdict]
  const agreesWithFinalVerdict = !agreeingVerdicts || (!!finalVerdict && agreeingVerdicts.includes(finalVerdict))

  return agreesWithFinalVerdict
    ? { signalVerdict: predictiveAIVerdictToSignalVerdict(predictiveAiVerdict), labelVerdict: predictiveAiVerdict }
    : { signalVerdict: SignalVerdict.Unknown, labelVerdict: PredictiveAIVerdict.Unknown }
}

/**
 * Converts scan result to result text
 *
 * @param threatName
 * @param resultIndex scan result index
 * @returns
 */
export const getScanResultText = (threatName = '', resultIndex = 255): string => {
  if (threatName) return threatName
  if (isBenign(resultIndex)) return 'benign'
  if (isInformational(resultIndex)) return 'no-threat'
  if (isSuspicious(resultIndex)) return 'suspicious'
  if (isMalicious(resultIndex)) return 'malicious'
  else return 'unknown'
}

/**
 * Returns formatted date
 *
 * @param date date from OPSWAT reputation / MetaDefender api response
 */
export const formatDate = (date: Date): string => {
  if (!date) return ''

  try {
    const isoString = new Date(date).toISOString()
    return `${isoString.substring(0, 19).replace('T', ' ')}Z`
  } catch (error) {
    logger.warn('Failed to format date: ', date)
    logger.warn(error)
    return ''
  }
}
